import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EmailAuthProvider,
  deleteUser,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { reauthErrorMessage } from '@/lib/firebase-errors';

/**
 * Self-service account deletion, with the re-authentication it actually needs.
 *
 * Firebase refuses `deleteUser` unless the session authenticated recently, and
 * this app's sessions are indefinite — `getAuth()` with no `setPersistence`,
 * and nothing forces a token refresh. `auth_time` advances only when Identity
 * Platform authenticates somebody (measured against the real dev project in
 * #1904; the emulator gets this wrong), so a returning user's token carries the
 * `auth_time` of whenever they last typed their password. Their first attempt
 * is therefore *expected* to fail with `auth/requires-recent-login`.
 *
 * The recency window is entirely server-side — there is no threshold in the
 * SDK — and was measured at between two and five minutes (#1920). So this is
 * not a rare edge: it is the normal path for anyone who did not sign in
 * moments ago.
 *
 * **Re-authenticate in place rather than sending them to sign out and back
 * in.** The page previously said "please sign in again" and offered no way to
 * do it: there is no sign-out control on Settings, so recovering meant
 * Dashboard → Sign Out → sign in → Settings → Danger Zone → retype DELETE, and
 * be quick about it. `OidcContinuePage` established the better pattern for the
 * OIDC step-up and the reasoning carries over — signing out to prove presence
 * strands anyone who abandons the prompt in a signed-out state they never asked
 * for. Firebase also asserts the credential belongs to the *same* uid, so
 * nobody can re-authenticate as a different account midway and delete that one
 * instead.
 *
 * The typed `DELETE` confirmation stays. An admin deleting somebody else's
 * account gets a two-stage modal with a reason field (`UserDeletionModal`); a
 * person deleting their own should not get less ceremony than that.
 *
 * There is no backend call here. `deleteUser` removes the Identity Platform
 * user and `onUserDeletedTrigger` writes the tombstone and clears the data
 * afterwards, so a partial failure leaves nothing half-deleted.
 */
export interface DeleteAccountSectionProps {
  /** Heading for the section. */
  heading: string;
  /** What deleting costs, in this page's terms. */
  description: ReactNode;
  /** Resting label for the destructive button. */
  actionLabel: string;
}

type Phase = 'idle' | 'deleting' | 'reauth';

export function DeleteAccountSection({
  heading,
  description,
  actionLabel,
}: DeleteAccountSectionProps) {
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);

  const busy = phase === 'deleting';
  const confirmed = confirmation === 'DELETE';

  /**
   * Attempt the delete. Called twice on the recency path — once optimistically,
   * and again after the password is confirmed — because the first failure is
   * the only way to learn the window has lapsed. Firebase exposes no way to ask
   * beforehand, so prompting for a password up front would demand one from
   * everybody to spare the few who need it.
   */
  const runDelete = async (): Promise<void> => {
    const current = auth.currentUser;
    if (!current) {
      setError(
        'You are no longer signed in. Sign in again to delete your account.'
      );
      setPhase('idle');
      return;
    }

    setPhase('deleting');
    setError(null);
    try {
      await deleteUser(current);
      // Deletion signs the user out, so there is no session to return to.
      navigate('/');
    } catch (err) {
      const code =
        typeof err === 'object' && err !== null && 'code' in err
          ? String((err as { code: unknown }).code)
          : '';

      if (code === 'auth/requires-recent-login') {
        // Not an error the person caused, and not one to apologise for — it is
        // the expected first outcome. Ask for the password and carry on.
        if (!current.email) {
          // No password credential to rebuild. Nothing in this app signs in
          // without one today, so this is a guard rather than a path.
          setError(
            'For security, deleting an account needs a recent sign-in. Sign out, sign in again, and retry.'
          );
          setPhase('idle');
          return;
        }
        setPassword('');
        setPhase('reauth');
        return;
      }

      setError(
        'Could not delete your account. Please try again, or contact support.'
      );
      setPhase('idle');
    }
  };

  const reauthenticateThenDelete = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const current = auth.currentUser;
    if (!current?.email) {
      setError(
        'You are no longer signed in. Sign in again to delete your account.'
      );
      setPhase('idle');
      return;
    }

    setPhase('deleting');
    setError(null);
    try {
      await reauthenticateWithCredential(
        current,
        EmailAuthProvider.credential(current.email, password)
      );
    } catch (err) {
      setError(reauthErrorMessage(err));
      setPhase('reauth');
      return;
    }

    setPassword('');
    await runDelete();
  };

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
        {heading}
      </h3>
      <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {description}
      </div>

      {phase === 'reauth' ? (
        <form
          onSubmit={e => void reauthenticateThenDelete(e)}
          className="space-y-4"
        >
          <p className="text-sm text-gray-700 dark:text-gray-300">
            For your security, confirm your password to finish deleting this
            account.
          </p>

          {/* Present for password managers, which need a username field beside
              the password one to offer the right credential. Not editable: this
              deletes the account you are signed in as, and Firebase refuses a
              credential belonging to any other. */}
          <div>
            <label
              htmlFor="delete-reauth-email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Signed in as
            </label>
            <input
              id="delete-reauth-email"
              name="username"
              type="email"
              autoComplete="username"
              readOnly
              value={auth.currentUser?.email ?? ''}
              className="input bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label
              htmlFor="delete-reauth-password"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Password
            </label>
            <input
              id="delete-reauth-password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              autoFocus
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={busy}
              className="input"
            />
          </div>

          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              disabled={busy}
              className="btn bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Deleting…' : 'Confirm and delete'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setPhase('idle');
                setPassword('');
                setError(null);
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div>
            <label
              htmlFor="delete-confirmation"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Type{' '}
              <span className="font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                DELETE
              </span>{' '}
              to confirm
            </label>
            <input
              type="text"
              id="delete-confirmation"
              value={confirmation}
              onChange={e => setConfirmation(e.target.value)}
              className="input"
              placeholder="Type DELETE"
            />
          </div>

          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}

          <button
            onClick={() => void runDelete()}
            disabled={!confirmed || busy}
            className="btn bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? 'Deleting…' : actionLabel}
          </button>
        </div>
      )}
    </div>
  );
}
